import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Lf23Loader from "../../components/Lf23Loader";
import { API_URL, authHeaders, formatData, statoBadgeClass, Stella } from "./comuni";
import { AzioniPercorso, fotoStatoBadgeClass, FOTO_ATTESE_PER_TRATTA, descriviRiapertura } from "./percorsiCondivisi";
import SezionePianificati from "./SezionePianificati";
import SezioneOggi from "./SezioneOggi";
import SezioneStorico from "./SezioneStorico";

// URL del proxy immagine per la galleria; w = larghezza richiesta (miniatura vs full).
const fotoImgUrl = (key, w) =>
    `${API_URL}/gestione-percorsi/percorsi/foto-img?key=${encodeURIComponent(key)}${w ? `&w=${w}` : ""}`;

/**
 * Tab "Percorsi" (community Interacta 122, SOLA LETTURA a parte il "Chiudi" dello Storico).
 * Tre sotto-tab per DATA, non più per stato (task 517):
 *  - Pianificati -> bozze da convalidare (SezionePianificati)
 *  - Oggi        -> i giri di oggi in qualunque stato + notturni già partiti (SezioneOggi)
 *  - Storico     -> avviso "Da chiudere" + conclusi con filtri e paginazione (SezioneStorico)
 *
 * Qui resta solo quello che le sezioni condividono: il sotto-tab attivo, la vista
 * tabella/card e i modali aperti da una riga (dettaglio vie, galleria foto, lightbox) più
 * il download del report PDF. Ogni sezione carica i suoi dati e ha il suo "Aggiorna".
 */
const SOTTO_TAB = [
    { key: "pianificati", label: "Pianificati" },
    { key: "oggi", label: "Oggi" },
    { key: "storico", label: "Storico" },
];

// Sotto-tab iniziale: "oggi", oppure quello chiesto dall'URL (?vista=storico è il link della
// mail delle 09:00 sui percorsi da chiudere). Un valore sconosciuto torna su "oggi".
const subIniziale = () => {
    const v = new URLSearchParams(window.location.search).get("vista");
    return SOTTO_TAB.some((s) => s.key === v) ? v : "oggi";
};

const TabPercorsi = ({ zona }) => {
    const [sub, setSub] = useState(subIniziale);

    // dettaglio percorso (modale al click sulla riga) + indice tratti per risolvere le vie
    const [detailTarget, setDetailTarget] = useState(null);
    const [trattiList, setTrattiList] = useState(null); // null = non ancora caricato (fallback nomi vie)
    // dettaglio vie del percorso aperto: [{ codice, strada, tratto, passAnno, fatto, nFoto }]
    const [detailRows, setDetailRows] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // download report PDF (per riga): id del percorso in download, per disabilitare il bottone
    const [reportLoadingId, setReportLoadingId] = useState(null);

    // galleria foto (modale al click su 📷)
    const [galleryTarget, setGalleryTarget] = useState(null); // percorso aperto
    const [galleryData, setGalleryData] = useState(null);      // { nome, customId, vie:[{codice,strada,tratto,fotoKeys}] }
    const [galleryLoading, setGalleryLoading] = useState(false);
    const [lightbox, setLightbox] = useState(null);            // foto_key ingrandito

    // vista dati: tabella (default da 1025px in su) oppure card (default da 1024px in giù).
    // Qui e non nelle sezioni: cambiando sotto-tab la scelta resta.
    const [viewMode, setViewMode] = useState(() => (window.matchMedia("(max-width: 1024px)").matches ? "cards" : "table"));

    // Cambio vista automatico al superamento della soglia 1024px, SENZA ricaricare:
    // <=1024 -> card, >=1025 -> tabella (riapplica il default della fascia).
    // "change" scatta solo attraversando la soglia, non a ogni resize.
    useEffect(() => {
        const mql = window.matchMedia("(max-width: 1024px)");
        const onChange = (e) => setViewMode(e.matches ? "cards" : "table");
        mql.addEventListener("change", onChange);
        return () => mql.removeEventListener("change", onChange);
    }, []);

    // ---- indice tratti (lazy): serve a risolvere i trattoPostIds in nomi di via ----
    const ensureTrattiList = useCallback(async () => {
        if (trattiList) return trattiList;
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/tratti`, {
                headers: authHeaders(),
                params: { page: 1, limit: 100000 },
            });
            const list = res.data?.data ?? [];
            setTrattiList(list);
            return list;
        } catch (err) {
            console.error("Errore nel caricamento dei tratti:", err);
            setTrattiList([]);
            return [];
        }
    }, [trattiList]);

    const openDetail = async (p) => {
        setDetailTarget(p);
        setDetailRows(null);
        setDetailLoading(true);
        try {
            // Vie arricchite dal backend: fatto/non fatto (segnato dall'autista) + n. foto per via + pass/anno.
            const res = await fetch(`${API_URL}/gestione-percorsi/percorsi/${p.id}/foto`, { headers: authHeaders() });
            if (!res.ok) throw new Error("dettaglio vie non disponibile");
            const json = await res.json();
            setDetailRows((json.vie || []).map((v) => ({
                codice: v.codice, strada: v.strada, tratto: v.tratto,
                passAnno: v.passAnno ?? null,
                fatto: v.fatto, nFoto: (v.fotoKeys || []).length,
                // Stella di recupero: "gialla"/"rossa" calcolata dal backend sullo storico
                // della tratta; prioritariaPost = stella scritta sul post al "Genera".
                priorita: v.priorita ?? null,
                volteConsecutive: v.volteConsecutive ?? 0,
                // Stella congelata sul post: è quella che vede l'autista sull'app mobile.
                prioritariaPost: !!v.prioritariaPost,
            })));
        } catch (err) {
            // Fallback (es. DB app mobile non raggiungibile): solo nomi vie dall'anagrafica.
            const list = await ensureTrattiList();
            const byId = new Map(list.map((t) => [t.id, t]));
            setDetailRows((p.trattoPostIds || []).map((id) => {
                const t = byId.get(id) || { id, codice: "?", strada: "(tratto non trovato)", tratto: "" };
                return {
                    codice: t.codice, strada: t.strada, tratto: t.tratto,
                    passAnno: t.passAnno ?? null,
                    fatto: undefined, nFoto: undefined,
                    priorita: null, volteConsecutive: 0, prioritariaPost: false,
                };
            }));
        } finally {
            setDetailLoading(false);
        }
    };

    // Scarica il PDF "Report Percorso" (info + vie + foto) dal backend.
    // Pattern blob -> object URL -> click su <a download>, come le altre export del progetto.
    const downloadReport = async (p) => {
        setReportLoadingId(p.id);
        try {
            const res = await fetch(`${API_URL}/gestione-percorsi/percorsi/${p.id}/report-pdf`, {
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error("Errore nella generazione del report");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const safe = String(p.customId || p.id).replace(/[^A-Za-z0-9_-]/g, "_");
            link.setAttribute("download", `report_percorso_${safe}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Errore download report:", err);
            alert("Impossibile generare il report del percorso. Riprova.");
        } finally {
            setReportLoadingId(null);
        }
    };

    // Apre la galleria: carica dal backend la lista foto (foto_key) del percorso.
    const openGallery = async (p) => {
        setGalleryTarget(p);
        setGalleryData(null);
        setLightbox(null);
        setGalleryLoading(true);
        try {
            const res = await fetch(`${API_URL}/gestione-percorsi/percorsi/${p.id}/foto`, { headers: authHeaders() });
            if (!res.ok) throw new Error("Errore nel caricamento delle foto");
            setGalleryData(await res.json());
        } catch (err) {
            console.error("Errore galleria foto:", err);
            setGalleryData({ vie: [], error: true });
        } finally {
            setGalleryLoading(false);
        }
    };
    const closeGallery = () => { setGalleryTarget(null); setLightbox(null); };

    // I bottoni azione di una riga, con i gestori di questo componente già legati.
    // opts.conReport = false sui giri ancora aperti (niente PDF di un giro non finito).
    const azioniDi = (p, opts = {}) => (
        <AzioniPercorso p={p} reportLoadingId={reportLoadingId} onReport={downloadReport} onGallery={openGallery} conReport={opts.conReport !== false} />
    );

    const propsSezione = { zona, viewMode, onViewMode: setViewMode, onDetail: openDetail, azioniDi };

    return (
        <>
            <div className="gp-subtabs">
                {SOTTO_TAB.map((s) => (
                    <button key={s.key} className={`gp-subtab ${sub === s.key ? "active" : ""}`} onClick={() => setSub(s.key)}>
                        {s.label}
                    </button>
                ))}
            </div>

            {sub === "pianificati" && <SezionePianificati zona={zona} viewMode={viewMode} onViewMode={setViewMode} />}
            {sub === "oggi" && <SezioneOggi {...propsSezione} />}
            {sub === "storico" && <SezioneStorico {...propsSezione} />}

            {/* ===== DETTAGLIO PERCORSO (read-only, al click sulla riga) ===== */}
            {detailTarget && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setDetailTarget(null); }}>
                    <div className="modal" style={{ maxWidth: 920 }}>
                        <div className="modal-header">
                            <div className="m-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 20l-5.5-2.5V4L9 6.5m0 13.5l6-3m-6 3V6.5m6 10.5l5.5 2.5V6L15 3.5m0 13.5V3.5" /></svg>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3>{detailTarget.nome || "—"}</h3>
                                <div className="h-sub">{detailTarget.customId || "—"}</div>
                            </div>
                            <button className="modal-close" onClick={() => setDetailTarget(null)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="gp-detail-grid">
                                <div><span className="gp-detail-label">Data</span><span className="gp-detail-val">{formatData(detailTarget.data)}</span></div>
                                <div><span className="gp-detail-label">Assegnatario</span><span className="gp-detail-val">{detailTarget.assegnatario?.nome || "—"}</span></div>
                                <div><span className="gp-detail-label">Itinerario</span><span className="gp-detail-val">{detailTarget.itinerario || "—"}</span></div>
                                <div><span className="gp-detail-label">Stato Percorso</span><span className={`badge ${statoBadgeClass(detailTarget.stato)}`}>{detailTarget.stato || "—"}</span></div>
                                <div><span className="gp-detail-label">Stato Foto</span><span className={`badge ${fotoStatoBadgeClass(detailTarget.fotoStato)}`}>{detailTarget.fotoStato || "—"}</span></div>
                                {/* Riaperto dall'app e non ancora richiuso (task 532). */}
                                {detailTarget.riapertura && (
                                    <div className="gp-detail-full"><span className="gp-detail-label">Riapertura</span><span className="gp-detail-val">{descriviRiapertura(detailTarget.riapertura)}: non ancora richiuso</span></div>
                                )}
                            </div>
                            <div className="section-label">
                                Vie del percorso ({detailTarget.nTratti})
                                {detailRows && detailRows.some((r) => r.fatto != null) && (
                                    <span className="gp-done-count"> — {detailRows.filter((r) => r.fatto).length}/{detailRows.length} fatte</span>
                                )}
                            </div>
                            <div className="gp-selected-box gp-detail-vie" style={{ height: "auto" }}>
                                {detailLoading ? (
                                    <div className="gp-box-empty">Caricamento vie...</div>
                                ) : !detailRows || detailRows.length === 0 ? (
                                    <div className="gp-box-empty">Nessuna via associata.</div>
                                ) : (
                                    detailRows.map((t, i) => (
                                        <div className={`tratto-row ${t.priorita === "rossa" ? "is-recupero-rossa" : ""} ${t.prioritariaPost ? "is-prioritaria" : ""}`} key={t.codice || i}>
                                            <div className="idx">{i + 1}</div>
                                            <div className="t-name"><b>{t.strada || "(senza strada)"}</b><span>{t.tratto} · {t.codice}{t.passAnno != null && <span className="gp-pass-badge">{t.passAnno} pass/anno</span>}</span></div>
                                            <div className="gp-via-meta">
                                                <Stella livello={t.priorita} volte={t.volteConsecutive} suPost={t.prioritariaPost} />
                                                {t.fatto != null && (
                                                    <span className={`badge ${t.fatto ? "badge-stato-completate" : "badge-stato-noneseguite"}`}>{t.fatto ? "Fatto" : "Non fatto"}</span>
                                                )}
                                                {t.nFoto != null && (
                                                    <span className="gp-foto-count" title="Foto scattate su attese">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="M21 15l-5-5L5 21" /></svg>
                                                        {t.nFoto}/{FOTO_ATTESE_PER_TRATTA}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <div />
                            <div className="footer-actions">
                                <button className="btn btn-ghost" onClick={() => setDetailTarget(null)}>Chiudi</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* ===== GALLERIA FOTO (modale al click su 📷) ===== */}
            {galleryTarget && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeGallery(); }}>
                    <div className="modal gp-gallery-modal" style={{ maxWidth: 780 }}>
                        <div className="modal-header">
                            <div className="m-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="M21 15l-5-5L5 21" /></svg>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3>Foto — {galleryTarget.nome || "—"}</h3>
                                <div className="h-sub">{galleryTarget.customId || "—"}</div>
                            </div>
                            <button className="modal-close" onClick={closeGallery}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            {galleryLoading ? (
                                <div className="gp-gallery-loading">
                                    <Lf23Loader testo="Caricamento foto in corso..." />
                                </div>
                            ) : !galleryData || galleryData.error ? (
                                <div className="gp-box-empty">Impossibile caricare le foto.</div>
                            ) : (galleryData.vie || []).filter((v) => (v.fotoKeys || []).length).length === 0 ? (
                                <div className="gp-box-empty">Nessuna foto disponibile per questo percorso.</div>
                            ) : (
                                (galleryData.vie || []).filter((v) => (v.fotoKeys || []).length).map((v, i) => (
                                    <div className="gp-gallery-via" key={v.codice || i}>
                                        <div className="gp-gallery-via-head">
                                            <b>{v.strada || "(senza strada)"}</b>
                                            <span>{[v.tratto, v.codice].filter(Boolean).join(" · ")}</span>
                                        </div>
                                        <div className="gp-gallery-grid">
                                            {v.fotoKeys.map((k) => (
                                                <img key={k} className="gp-gallery-thumb" src={fotoImgUrl(k, 500)} alt="foto" loading="lazy" onClick={() => setLightbox(k)} />
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Lightbox: foto ingrandita a tutto schermo */}
            {lightbox && (
                <div className="gp-lightbox" onClick={() => setLightbox(null)}>
                    <button className="gp-lightbox-close" onClick={() => setLightbox(null)} title="Chiudi">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                    <img src={fotoImgUrl(lightbox)} alt="foto ingrandita" onClick={(e) => e.stopPropagation()} />
                </div>
            )}
        </>
    );
};

export default TabPercorsi;
